import { formatCurrency, getMinimumPrice } from '../../utils/currency';
import type { WizardData, WizardVariant } from './wizardTypes';

// The free trials on offer; the server compares trials up to 30 days.
export const TRIAL_CHOICES = [0, 3, 7, 14, 30];
export const MAX_TRIAL_DAYS = 30;

/** A one-time price has no trial, and a test can't wait out one past the limit. */
export function canChangeTrial(control: Pick<WizardVariant, 'interval' | 'trialDays'>): boolean {
  return control.interval !== 'one_time' && control.trialDays <= MAX_TRIAL_DAYS;
}

/** What the seller should know about the offers as they stand: at most two short lines. */
export function offerNotes(variants: Array<Pick<WizardVariant, 'price' | 'trialDays'>>): string[] {
  const [control, ...challengers] = variants;
  if (!control || !challengers.some((v) => v.trialDays !== control.trialDays)) return [];
  const changedBoth = challengers.some((v) => v.price !== control.price && v.trialDays !== control.trialDays);
  return [
    'Results wait for first payments after the free trial.',
    ...(changedBoth ? ["You'll learn which offer earns more, not which change did it."] : []),
  ];
}

// Whop refuses a plan over $2,500 USD unless the account has been raised.
const MAX_USD_PRICE = 250000;

/** The most Whop takes for a price in this currency, when we know it. */
export function maxPrice(currency: string): number | null {
  return currency.toLowerCase() === 'usd' ? MAX_USD_PRICE : null;
}

/** Why one price can't be used, or null. */
export function priceProblem(price: number, currency: string): string | null {
  const min = getMinimumPrice(currency);
  if (price < min) return `The lowest price is ${formatCurrency(min, currency)}.`;
  const max = maxPrice(currency);
  if (max !== null && price > max) return `Whop's limit is ${formatCurrency(max, currency)}.`;
  return null;
}

/** Why step 1 can't continue yet, in words for the line beside Continue. Null when ready. */
export function priceTestProblem(data: WizardData): string | null {
  if (!data.productId) return 'Pick a product to continue.';
  const first = data.variants.map((v) => priceProblem(v.price, data.currency)).find(Boolean);
  if (first) return first;
  const offers = data.variants.map((v) => `${v.price}|${v.trialDays}|${v.promoCodeId ?? ''}`);
  if (new Set(offers).size !== offers.length) return 'Each offer in the test has to be different.';
  const split = Object.values(data.split).reduce((a, b) => a + b, 0);
  if (split !== 100) return 'The split has to add up to 100%. Fix it under More options.';
  if (!data.name.trim()) return 'Give the test a name under More options.';
  return null;
}
